import React, { useEffect, useState } from "react";
import { Card, Grid, Tab, Tabs, Chip, TextField, InputAdornment } from "@mui/material";
import { SearchIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { knowledgeActions } from "store/knowledge-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllDocuments,
  fetchAllTags,
  downloadDocument,
} from "app/services/knowledge.service";
import { DocumentDataGrid } from "./DocumentDataGrid";

export const KnowledgeManagerView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  const documents = useSelector((state) => state.knowledge.documents);
  const tags = useSelector((state) => state.knowledge.tags);

  const loadData = () => {
    setIsLoading(true);
    Promise.all([
      fetchAllDocuments().then((data) => dispatch(knowledgeActions.setDocuments(data))),
      fetchAllTags().then((data) => dispatch(knowledgeActions.setTags(data))),
    ])
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (doc) => {
    try {
      const blob = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || `${doc.documentTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  const publishedDocs = documents.filter((d) => d.status === "PUBLISHED");
  const recentDocs = [...publishedDocs].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 10);

  const filteredDocs = publishedDocs.filter((doc) => {
    const matchesSearch = !searchQuery ||
      doc.documentTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "ALL" || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ["ALL", "SOP", "POLICY", "TEMPLATE", "GUIDE", "ARTICLE", "OTHER"];

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">Available</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{publishedDocs.length}</MDTypography>
              <MDTypography variant="caption">Published Documents</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">SOPs</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{publishedDocs.filter(d => d.category === "SOP").length}</MDTypography>
              <MDTypography variant="caption">Standard Procedures</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Policies</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{publishedDocs.filter(d => d.category === "POLICY").length}</MDTypography>
              <MDTypography variant="caption">Company Policies</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="secondary">Tags</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{tags.length}</MDTypography>
              <MDTypography variant="caption">Categories</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="All Documents" />
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  Recent Updates
                  {recentDocs.length > 0 && <Chip size="small" label={recentDocs.length} color="info" />}
                </MDBox>} />
              </Tabs>
            </MDBox>

            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    size="small"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    {categories.map((cat) => (
                      <Chip
                        key={cat}
                        label={cat}
                        onClick={() => setSelectedCategory(cat)}
                        color={selectedCategory === cat ? "primary" : "default"}
                        variant={selectedCategory === cat ? "filled" : "outlined"}
                        size="small"
                      />
                    ))}
                  </MDBox>
                </MDBox>
                <DocumentDataGrid documents={filteredDocs} isLoading={isLoading} onDownload={handleDownload} showActions={false} />
              </MDBox>
            )}
            {tabValue === 1 && (
              <DocumentDataGrid documents={recentDocs} isLoading={isLoading} onDownload={handleDownload} showActions={false} />
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

